/**
 * G2eGente - Criar/Editar Diagnóstico NR01
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { nr01API, employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft,
  Save,
  Play,
  Loader2,
  ShieldCheck,
  Building2,
  FileText,
  CheckCircle2,
  Info,
  Plus,
  X,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

export default function NR01DiagnosticForm() {
  const navigate = useNavigate();
  const { diagnosticId } = useParams();
  const isEditing = Boolean(diagnosticId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [dimensoes, setDimensoes] = useState([]);
  const [perguntas, setPerguntas] = useState([]);

  const [form, setForm] = useState({
    titulo: '',
    descricao: '',
    setores: [],
    setores_customizados: [],
    data_inicio: '',
    data_fim: ''
  });
  
  const [novoSetor, setNovoSetor] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, [diagnosticId]);

  const fetchInitialData = async () => {
    try {
      // Buscar dimensões e perguntas padrão
      const [dimRes, deptRes] = await Promise.all([
        nr01API.getDimensoes(),
        employeesAPI.getDepartments()
      ]);
      
      setDimensoes(dimRes.data.dimensoes || []);
      setPerguntas(dimRes.data.perguntas || []);
      setDepartments(deptRes.data.departments || []);
      
      if (isEditing) {
        const { data } = await nr01API.getById(diagnosticId);
        setForm({
          titulo: data.titulo || '',
          descricao: data.descricao || '',
          setores: data.setores || [],
          setores_customizados: data.setores_customizados || [],
          data_inicio: data.data_inicio || '',
          data_fim: data.data_fim || ''
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      navigate('/company/nr01');
    } finally {
      setLoading(false);
    }
  };

  const handleSetorToggle = (setor) => {
    setForm(prev => ({
      ...prev,
      setores: prev.setores.includes(setor)
        ? prev.setores.filter(s => s !== setor)
        : [...prev.setores, setor]
    }));
  };

  const handleSelectAllSetores = () => {
    setForm(prev => ({
      ...prev,
      setores: prev.setores.length === departments.length ? [] : [...departments]
    }));
  };
  
  const handleAddCustomSetor = () => {
    if (!novoSetor.trim()) return;
    if (form.setores_customizados.includes(novoSetor.trim())) {
      toast.error('Este setor já foi adicionado');
      return;
    }
    setForm(prev => ({
      ...prev,
      setores_customizados: [...prev.setores_customizados, novoSetor.trim()]
    }));
    setNovoSetor('');
  };
  
  const handleRemoveCustomSetor = (setor) => {
    setForm(prev => ({
      ...prev,
      setores_customizados: prev.setores_customizados.filter(s => s !== setor)
    }));
  };

  const handleSave = async (activate = false) => {
    if (!form.titulo.trim()) {
      toast.error('Informe o título do diagnóstico');
      return;
    }
    
    const totalSetores = form.setores.length + form.setores_customizados.length;
    if (totalSetores === 0) {
      toast.error('Selecione ou adicione pelo menos um setor');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await nr01API.update(diagnosticId, form);
        if (activate) {
          await nr01API.update(diagnosticId, { status: 'ativo' });
          toast.success('Diagnóstico salvo e ativado!');
        } else {
          toast.success('Diagnóstico salvo');
        }
      } else {
        const { data } = await nr01API.create(form);
        if (activate) {
          await nr01API.update(data.id, { status: 'ativo' });
          toast.success('Diagnóstico criado e ativado!');
        } else {
          toast.success('Diagnóstico criado como rascunho');
        }
      }
      navigate('/company/nr01');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao salvar diagnóstico';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/company/nr01')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-amber-600" />
            {isEditing ? 'Editar Diagnóstico' : 'Novo Diagnóstico NR-01'}
          </h1>
          <p className="text-slate-500">Configure os setores a serem avaliados</p>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-amber-600" />
              Informações do Diagnóstico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="titulo">Título *</Label>
              <Input
                id="titulo"
                value={form.titulo}
                onChange={(e) => setForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ex: Diagnóstico NR-01 - 1º Semestre 2025"
                data-testid="nr01-titulo"
              />
            </div>
            <div>
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Textarea
                id="descricao"
                value={form.descricao}
                onChange={(e) => setForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descreva o objetivo deste diagnóstico..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="data_inicio">Data de início (opcional)</Label>
                <Input
                  id="data_inicio"
                  type="date"
                  value={form.data_inicio}
                  onChange={(e) => setForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="data_fim">Data de término (opcional)</Label>
                <Input
                  id="data_fim"
                  type="date"
                  value={form.data_fim}
                  onChange={(e) => setForm(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Setores Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-amber-600" />
              Setores a Avaliar (GHE)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Selecione os setores/departamentos que serão incluídos neste diagnóstico. 
              Cada setor receberá sua própria análise de riscos.
            </p>
            
            {departments.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">Nenhum departamento cadastrado</p>
                <p className="text-sm text-slate-400">Cadastre funcionários com departamentos para visualizar aqui</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-slate-600">
                    {form.setores.length} de {departments.length} selecionados
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllSetores}
                  >
                    {form.setores.length === departments.length ? 'Desmarcar todos' : 'Selecionar todos'}
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {departments.map(dept => (
                    <label
                      key={dept}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        form.setores.includes(dept)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-amber-300'
                      }`}
                    >
                      <Checkbox
                        checked={form.setores.includes(dept)}
                        onCheckedChange={() => handleSetorToggle(dept)}
                      />
                      <span className={form.setores.includes(dept) ? 'text-amber-800 font-medium' : 'text-slate-700'}>
                        {dept}
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
            
            {/* Custom Sectors - PGR/LTCAT */}
            <Separator className="my-6" />
            
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-amber-600" />
                <h4 className="font-semibold text-slate-700">Ambientes Adicionais (PGR/LTCAT)</h4>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Adicione setores ou ambientes de trabalho seguindo as descrições do PGR ou LTCAT 
                que estão ou não vinculados ao cadastro de funcionários. Importante para as avaliações do psicólogo.
              </p>
              
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Ex: Galpão de Produção, Sala de Máquinas, Escritório Central..."
                  value={novoSetor}
                  onChange={(e) => setNovoSetor(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCustomSetor()}
                  className="flex-1"
                  data-testid="custom-sector-input"
                />
                <Button
                  type="button"
                  onClick={handleAddCustomSetor}
                  disabled={!novoSetor.trim()}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="add-custom-sector-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              {form.setores_customizados.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.setores_customizados.map(setor => (
                    <Badge
                      key={setor}
                      className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer px-3 py-1.5"
                      onClick={() => handleRemoveCustomSetor(setor)}
                      data-testid={`custom-sector-${setor}`}
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      {setor}
                      <X className="w-3 h-3 ml-2" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Questions Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
              Questionário Padrão G2eGente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>35 perguntas validadas cientificamente</strong> baseadas no HSE-IT e COPSOQ, 
                  organizadas em 7 dimensões de risco psicossocial. Tempo médio de resposta: 8 minutos.
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensoes.map(dim => {
                const perguntasDim = perguntas.filter(p => p.dimensao === dim.id);
                return (
                  <div key={dim.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-slate-800">{dim.nome}</h4>
                      <Badge variant="outline">{perguntasDim.length} perguntas</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mb-3">{dim.descricao}</p>
                    <div className="space-y-1">
                      {perguntasDim.slice(0, 2).map(p => (
                        <p key={p.id} className="text-xs text-slate-400 pl-3 border-l-2 border-slate-200">
                          {p.texto}
                        </p>
                      ))}
                      {perguntasDim.length > 2 && (
                        <p className="text-xs text-slate-400 italic pl-3">
                          ... e mais {perguntasDim.length - 2} perguntas
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Legal Basis */}
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <h4 className="font-semibold text-slate-700 mb-3">Base Legal</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• <strong>NR-01</strong> (Portaria MTE 1.419/2024) - Gerenciamento de Riscos Ocupacionais</li>
              <li>• <strong>PGR</strong> - Programa de Gerenciamento de Riscos - Riscos Psicossociais</li>
              <li>• <strong>HSE Management Standards</strong> - Benchmarks de classificação</li>
              <li>• <strong>Guia MTE 2025</strong> - Orientações para avaliação psicossocial</li>
            </ul>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/company/nr01')}>
            Cancelar
          </Button>
          <Button 
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar Rascunho
          </Button>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => handleSave(true)}
            disabled={saving}
            data-testid="save-and-activate-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Salvar e Ativar
          </Button>
        </div>
      </div>
    </div>
  );
}
